/**
 * Async action button — prevents duplicate clicks while an async handler runs.
 *
 * @example Controlled (hook + explicit pending)
 * const { execute, isPending } = useAsyncAction(async () => {
 *   await apiPost('/api/items', payload);
 * });
 * <AsyncButton onClick={execute} pending={isPending} loadingText="Saving…">
 *   Save
 * </AsyncButton>
 *
 * @example Self-contained (internal pending)
 * <AsyncButton
 *   loadingText="Submitting…"
 *   onClick={async () => { await submitTimesheet(); }}
 * >
 *   Submit
 * </AsyncButton>
 */
import * as React from 'react';

import { Button } from './button';
import { useAsyncAction } from '../../hooks/useAsyncAction';

const AsyncButton = React.forwardRef(
  (
    {
      onClick,
      loading = false,
      loadingText,
      pending: pendingProp,
      disabled = false,
      children,
      type = 'button',
      ...buttonProps
    },
    ref
  ) => {
    const isControlled = pendingProp !== undefined || loading;
    const { execute, isPending: internalPending } = useAsyncAction(
      isControlled ? null : onClick
    );

    const pending = pendingProp ?? (loading || internalPending);

    const handleClick = (event) => {
      if (pending) {
        return;
      }
      if (isControlled) {
        onClick?.(event);
        return;
      }
      execute(event);
    };

    return (
      <Button
        ref={ref}
        type={type}
        loading={pending}
        disabled={disabled}
        onClick={handleClick}
        {...buttonProps}
      >
        {pending ? (loadingText ?? null) : children}
      </Button>
    );
  }
);

AsyncButton.displayName = 'AsyncButton';

export { AsyncButton };
